// Copyright 2016 Sorama B.V.

import { IIndexableObject } from "libraries/IndexedDb/IIndexableObject";
import { IndexedDb } from "libraries/IndexedDb/IndexedDb";
import { IPagedRequest, IPageResult, IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { PagedRequest } from "libraries/WebRequestHandler/PagedRequest";
import { IGenericRepository, IPagedGenericRepository } from "repositories/IGenericRepository";
import { Repository } from "repositories/Repository";

export abstract class GenericWebApiRepository<TType, TNewType>
    extends Repository
    implements IPagedGenericRepository<TType, TNewType>
{
    protected readonly baseUri: string;

    private updatePromise: Promise<TType[]>;

    constructor(
        protected readonly webRequestHandler: IWebRequestHandler,
        baseUri: string
    ) {
        super();
        this.webRequestHandler = webRequestHandler;
        this.baseUri = baseUri;
    }

    public getAll(): Promise<TType[]> {
        return this.get().readToEnd();
    }

    public get(odataQuery?: string): IPagedRequest<TType> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<TType>>(
            this.baseUri + (odataQuery ? "?" + odataQuery : "")
        );
        return PagedRequest.create<TType>(request);
    }

    public getById(id: string): Promise<TType> {
        return this.webRequestHandler
            .createGetRequest<TType>(this.baseUri + "/" + id)
            .send()
            .then((x) => x.content);
    }

    public put(id: string, obj: TNewType): Promise<TType> {
        return this.webRequestHandler
            .createPutRequest<TType>(this.baseUri + "/" + id, obj)
            .send()
            .then((x) => x.content);
    }

    public putProperties(id: string, newProperties: unknown): Promise<TType> {
        return this.webRequestHandler
            .createPutRequest<TType>(this.baseUri + "/" + id, newProperties)
            .send()
            .then((x) => x.content);
    }

    public post(obj: TNewType): Promise<TType> {
        return this.webRequestHandler
            .createPostRequest<TType>(this.baseUri, obj)
            .send()
            .then((x) => x.content);
    }

    public delete(id: string): Promise<void> {
        return this.webRequestHandler
            .createDeleteRequest<void>(this.baseUri + "/" + id)
            .send()
            .then(() => {});
    }

    protected equals(a: TType, b: TType): boolean {
        return false;
    }
}

export abstract class GenericIndexedDBRepository<TType extends IIndexableObject> implements IGenericRepository<
    TType,
    TType
> {
    public items: TType[];

    private indexedDb: IndexedDb;
    private objectStoreName: string;

    constructor(indexedDb: IndexedDb, objectStoreName: string) {
        this.indexedDb = indexedDb;
        this.objectStoreName = objectStoreName;
        this.items = [];
    }

    public getAll(): Promise<TType[]> {
        return this.indexedDb.getAll<TType>(this.objectStoreName).then((objects) => objects.map(this.fromDatabase));
    }

    public getById(id: string): Promise<TType> {
        return this.indexedDb.getObject<TType>(id, this.objectStoreName).then(this.fromDatabase);
    }

    public putMany(objs: TType[]): Promise<TType[]> {
        for (const obj of objs) {
            if (!obj.id) {
                throw new Error("Object contains no id");
            }
        }

        const promises: Promise<TType>[] = [];

        for (const obj of objs) {
            const promise = this.indexedDb.putObject(obj, this.objectStoreName).then((key) => {
                // Set the object key for autogenerated key db's
                if (obj.id !== key) obj.id = key;

                return obj;
            });

            promises.push(promise);
        }

        return Promise.all(promises).then((obj) => this.updateItems().then(() => obj));
    }

    public put(obj: TType, id: string = undefined): Promise<TType> {
        if (id) {
            obj.id = id;
        }

        if (!obj.id) {
            throw new Error("Object contains no id");
        }

        return this.indexedDb
            .putObject(obj, this.objectStoreName)
            .then((key) => {
                // Set the object key for autogenerated key db's
                if (obj.id !== key) obj.id = key;

                return obj;
            })
            .then((obj) => this.updateItems().then(() => obj));
    }

    public putProperties(id: string, newProperties: unknown): Promise<TType> {
        return this.getById(id)
            .then((obj) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const objectAny = obj as any;

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                for (const key in newProperties as any) {
                    if (Object.prototype.hasOwnProperty.call(newProperties, key)) {
                        objectAny[key] = newProperties[key];
                    }
                }

                return this.indexedDb.putObject(objectAny, this.objectStoreName).then((key) => {
                    // Set the object key for autogenerated key db's
                    objectAny.id = key;
                    return objectAny;
                });
            })
            .then((obj) => this.updateItems().then(() => obj));
    }

    public post(obj: TType): Promise<TType> {
        return this.indexedDb
            .addObject(obj, this.objectStoreName)
            .then((key) => {
                // Set the object key for autogenerated key db's
                obj.id = key;
                return obj;
            })
            .then((obj) => this.updateItems().then(() => obj));
    }

    public delete(id: string): Promise<void> {
        return this.indexedDb.removeObject(id, this.objectStoreName).then(() => this.updateItems().then(() => {}));
    }

    public deleteAll(): Promise<void> {
        return this.indexedDb.clearObjectStore(this.objectStoreName);
    }

    protected abstract fromDatabase(obj: TType): TType;

    private updateItems(): Promise<TType[]> {
        return this.getAll().then((objects) => {
            this.items.splice(0, this.items.length, ...objects);
            return this.items;
        });
    }
}
